import React from 'react'

import ErrorToast from '../../components/molecules/ErrorToast'

import {
  getPermissions,
  getRoles,
  createRole,
  editRole,
  deleteRole
} from '../../lib/api'
import { initialState, Actions, RolesPermissionsReducer } from './reducer'

export const RolesPermissionsContext = React.createContext(null)

const INITIAL_OBJECT = {
  id: null,
  name: '',
  details: '',
  permissions: [],
  system: false
}

const RolesPermissionsProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(
    RolesPermissionsReducer,
    initialState
  )
  const [openModal, setOpenModal] = React.useState(false)
  const [data, setData] = React.useState({})
  const [toSaveObject, setToSaveObject] = React.useState(INITIAL_OBJECT)
  const [error, setError] = React.useState(null)

  React.useEffect(() => {
    if (data) {
      setToSaveObject(data)
    } else {
      setToSaveObject(INITIAL_OBJECT)
    }
  }, [data])

  const fetchPermissions = React.useCallback(async () => {
    try {
      const permissions = await getPermissions()
      dispatch({ type: Actions.SET_PERMISSIONS, permissions: permissions })
    } catch (err) {
      console.error('Error fetching permissions', err)
      setError({ err })
    }
  }, [])

  const fetchRoles = React.useCallback(async () => {
    try {
      const roles = await getRoles()

      dispatch({ type: Actions.SET_ROLES, roles: roles })
    } catch (err) {
      console.error('Error fetching roles', err)
      setError({ err })
    }
  }, [])

  const updatingRoles = React.useCallback(async () => {
    const { id, name, details, permissions } = toSaveObject

    if (!id) {
      // NEED TO DISPLAY ERROR
      if (!details || !name || !details?.length || !name?.length) {
        setError({ err: { message: 'Name or details input cannot be empty' } })
        return false
      }

      // add role then update provider
      const data = {
        name: name,
        details: details,
        permissions: permissions
      }

      try {
        const newRole = await createRole(data)
        dispatch({ type: Actions.UPDATE_ROLE, editRole: newRole })
        return true
      } catch (err) {
        console.error('Error creating role', err)
        setError({ err })
      }
    } else {
      const data = {
        name: name,
        details: details,
        permissions: permissions
      }

      try {
        const edit = await editRole(id, data)

        dispatch({ type: Actions.UPDATE_ROLE, editRole: edit })
        return true
      } catch (err) {
        console.error('Error editing role', err)
        setError({ err })
      }
    }
  }, [toSaveObject])

  const removeRole = React.useCallback(async id => {
    try {
      await deleteRole(id)

      dispatch({ type: Actions.DELETE_ROLE, removeId: id })
      return true
    } catch (err) {
      console.error('Error deleting role', err)
      setError({ err })
      return false
    }
  }, [])

  return (
    <>
      <RolesPermissionsContext.Provider
        value={{
          fetchPermissions,
          fetchRoles,
          state,
          setOpenModal,
          openModal,
          setToSaveObject,
          toSaveObject,
          setData,
          data,
          updatingRoles,
          error,
          removeRole,
          setError
        }}
      >
        {children}
      </RolesPermissionsContext.Provider>

      <ErrorToast error={error} />
    </>
  )
}

export default RolesPermissionsProvider

export const useRolesPermissions = () =>
  React.useContext(RolesPermissionsContext)
